import { isServer } from '@vue-storefront/core/helpers'
import config from 'config'
import CheckoutEcommerce from '../types/CheckoutEcommerce'

const GRAND_TOTAL_SEGMENT_CODE = 'grand_total'
const PRICE_DECIMAL_PRECISION = 2

export const CHECKOUT_VARIANT_KUSTOM = 'kustom'
export const CHECKOUT_VARIANT_CURRENT = 'current'

const TRACKED_EVENTS_STORAGE_KEY = 'gtm_tracked_events'

const claimedInMemory = new Set<string>()

export const isEnabled = (gtmId: string | null) => {
  return typeof gtmId === 'string' && gtmId.length > 0 && !isServer
}

export const getGtmSource = (): string => {
  const serverUrl = config.googleTagManager.serverUrl
  const customLoader = config.googleTagManager.customLoader
  if (serverUrl && customLoader) {
    return `${serverUrl}/${customLoader}`
  }
  if (serverUrl) {
    return `${serverUrl}/gtm.js`
  }
  if (!config.googleTagManager.firstParty) {
    return 'https://www.googletagmanager.com/gtm.js'
  }
  return `${window.location.origin}/analytics/gtm.js`
}

export const getProduct = (item) => {
  let product = {}
  if (item) {
    const attributeMap: string[]|Record<string, any>[] = config.googleTagManager.product_attributes
    attributeMap.forEach(attribute => {
      const isObject = typeof attribute === 'object'
      let attributeField = isObject ? Object.keys(attribute)[0] : attribute
      let attributeName = isObject ? Object.values(attribute)[0] : attribute

      if (item.hasOwnProperty(attributeField) || product.hasOwnProperty(attributeName)) {
        const value = item[attributeField] || product[attributeName]
        if (value) {
          product[attributeName] = value
        }
      }
    })

    const { category } = item
    if (category && category.length > 0) {
      product['category'] = category.slice(-1)[0].name
    }

    if (product && !product.hasOwnProperty('currency')) {
      product['currency'] = config.googleTagManager.default_currency
    }
  }

  return product
}

/**
 * @param {Record<string, any>[]} totals
 * @return {number|undefined}
 */
export const getGrandTotal = (totals: Record<string, any>[]): number | undefined => {
  const segment = Array.isArray(totals)
    ? totals.find(total => total && total.code === GRAND_TOTAL_SEGMENT_CODE)
    : null

  if (!segment || segment.value === null || segment.value === undefined || segment.value === '') {
    return undefined
  }

  const value = Number(segment.value)

  return Number.isFinite(value) ? Number(value.toFixed(PRICE_DECIMAL_PRECISION)) : undefined
}

/**
 * @param {Record<string, any>[]} products
 * @param {Record<string, any>[]} totals
 * @return {CheckoutEcommerce}
 */
export const getCheckoutEcommerce = (
  products: Record<string, any>[],
  totals: Record<string, any>[]
): CheckoutEcommerce => {
  const value = getGrandTotal(totals)

  return {
    currency: config.googleTagManager.default_currency,
    ...(value !== undefined ? { value } : {}),
    items: (products || []).map(product => getProduct(product))
  }
}

/**
 * @param {string} eventName
 * @param {string} checkoutId
 * @return {boolean}
 */
export const claimOncePerCheckout = (eventName: string, checkoutId: string): boolean => {
  if (!checkoutId) {
    return false
  }

  const claim = `${eventName}_${checkoutId}`

  try {
    const stored = JSON.parse(sessionStorage.getItem(TRACKED_EVENTS_STORAGE_KEY) || '{}')
    const events = stored.checkoutId === checkoutId && Array.isArray(stored.events) ? stored.events : []

    if (events.indexOf(eventName) !== -1) {
      return false
    }

    sessionStorage.setItem(
      TRACKED_EVENTS_STORAGE_KEY,
      JSON.stringify({ checkoutId, events: events.concat(eventName) })
    )

    return true
  } catch (error) {
    if (claimedInMemory.has(claim)) {
      return false
    }
    claimedInMemory.add(claim)

    return true
  }
}
